import {
  retrieveFile,
  retrieveFileTree,
  updateFile,
} from "@/lib/data/requests/files/canonical-files";
import {
  adaptFile,
  mergeFileTree,
} from "@/lib/data/requests/files/file-adapter";
import { authenticatedFastApiAxiosInstanceAllowEmptyString } from "@/lib/data/http";
import { baseFastApiFirmRoute } from "@/lib/data/requests/firm/firm";
import type { APIResponse, FirmFile } from "@/lib/types";
import { authenticatedFastApiAxiosInstance } from "@/lib/data/http";

const firmFileWriteKeys = new Set([
  "artifactId",
  "description",
  "idLocation",
  "name",
  "presignedUrlKey",
  "tags",
]);
const cleanFirmFileBody = (data: object, update = false) =>
  Object.fromEntries(
    Object.entries(data).filter(
      ([key, value]) =>
        value !== undefined &&
        (firmFileWriteKeys.has(key) ||
          (update
            ? key === "id" || key === "dateModified"
            : key === "isFolder")),
    ),
  );

type FirmFileFilters = {
  exactName: string;
  idLocation?: string | null;
};

export const retrieveFirmFileMetadata = (filters?: FirmFileFilters) =>
  authenticatedFastApiAxiosInstance
    .get<APIResponse<FirmFile[]>>(`${baseFastApiFirmRoute}/files`, {
      params: filters,
    })
    .then((res) => res.data);

export const retrieveFirmFilesV1 = async (filters?: FirmFileFilters) => {
  if (filters) return retrieveFirmFileMetadata(filters);
  const [response, canonical] = await Promise.all([
    retrieveFirmFileMetadata(),
    retrieveFileTree({ scope: "firm" }),
  ]);
  return { ...response, data: mergeFileTree(canonical, response.data) };
};

export const createFirmFileV1 = (
  data: Pick<FirmFile, "name" | "isFolder"> &
    Partial<Pick<FirmFile, "idLocation">> & {
      file?: File;
    } & Partial<FirmFile>,
) => {
  const body = cleanFirmFileBody(data);

  return authenticatedFastApiAxiosInstanceAllowEmptyString
    .post<APIResponse<FirmFile>>(`${baseFastApiFirmRoute}/files`, body)
    .then((res) => res.data);
};

export const updateFirmFileV1 = async (
  data: Pick<FirmFile, "id" | "dateModified"> & Partial<FirmFile>,
) => {
  if (
    data.revision !== undefined &&
    data.presignedUrlKey === undefined &&
    !("artifactId" in data) &&
    data.tags === undefined
  ) {
    const current = await retrieveFile(data.id);
    if (
      "idLocation" in data &&
      (data.idLocation || null) !== current.parentId
    ) {
      return authenticatedFastApiAxiosInstanceAllowEmptyString
        .patch<
          APIResponse<FirmFile>
        >(`${baseFastApiFirmRoute}/files`, cleanFirmFileBody(data, true))
        .then((res) => res.data);
    }
    const file = await updateFile(data.id, {
      revision: data.revision,
      name: data.name,
      description: data.description,
    });
    return {
      data: adaptFile<FirmFile>(file, data),
      meta: { userMsg: "File updated" },
    };
  }
  return authenticatedFastApiAxiosInstanceAllowEmptyString
    .patch<
      APIResponse<FirmFile>
    >(`${baseFastApiFirmRoute}/files`, cleanFirmFileBody(data, true))
    .then((res) => res.data);
};

export const generateFirmFilePdfV1 = (data: { id: string; name: string }) => {
  return authenticatedFastApiAxiosInstanceAllowEmptyString
    .post<
      APIResponse<FirmFile>
    >(`${baseFastApiFirmRoute}/files/generate-pdf`, data)
    .then((res) => res.data);
};

export type GenerateFirmFileDocumentV1Input = {
  extension: ".docx" | ".pdf";
  idLocation: string;
  name: string;
  id: FirmFile["id"];
  inputs: { [key: string]: string };
};

export const generateFirmFileDocumentV1 = (
  data: GenerateFirmFileDocumentV1Input,
) => {
  return authenticatedFastApiAxiosInstanceAllowEmptyString
    .post<
      APIResponse<FirmFile>
    >(`${baseFastApiFirmRoute}/files/generate-document`, data)
    .then((res) => res.data);
};
